import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from '../localstorage.service';
import { UserService } from 'app/core/user/user.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { AnalyticsService } from '../analytics/analytics.service';
import { MessagesService } from 'app/layout/common/messages/messages.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { TimezonePickerService } from '../timezone/timezone-picker.service';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';
import { PaymentsService } from '../payments/payments.service';
import { TimezoneChangedPopupComponent } from 'app/components/timezone-changed-popup/timezone-changed-popup.component';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
@Injectable({
    providedIn: 'root'
})
export class DefaultDataService
{
    faviconTag: HTMLLinkElement = document.querySelector('#faviconTag');
    private popupShown = false; 
    
    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
                private _authService: AuthService,
                private _locationStrategy: LocationStrategy,
                private _localStorageService: LocalStorageService,
                private _userService: UserService,
                private _navigationService: NavigationService,
                private _analyticsService: AnalyticsService,
                private _messagesService: MessagesService,
                private _notificationsService: NotificationsService,
                private timezonePickerService: TimezonePickerService,
                private utilityService: UtilityService,
                private paymentService: PaymentsService,
                private zuperDialogService: ZuperDialogService,
            )
    {}

    public get baseUrl(): string {
        return `${localStorage.getItem('dc_api_url')}/api`;
    }

    getUserData(userData): void {
        // eslint-disable-next-line max-len
        this._httpClient.get(`${this.baseUrl}/access_role_permission/${userData.access_role.access_role_uid}`)
            .subscribe((response: any) => {
                this._localStorageService.setLocalStorage('user_permissions', response.data);
                this.getAndSetNavigation();
            });
    }

    getAndSetNavigation(): void{
        this._navigationService.getAllNavigation();
    }

    getSynchistoryModules(): void {
        const dcRegion: string = localStorage?.getItem('dc_region') || '';
        const isProd: boolean = environment.production;
        const url = isProd ? 'https://apps.zuperpro.com' : `${localStorage.getItem('dc_api_url')}/service/integrations`;
        this._httpClient.get(`${url}/plugin_sync_history/modules${dcRegion ? '?region=' + dcRegion : ''}`)
            .subscribe((response: any) => {
                this._localStorageService.setLocalStorage('sync_history_modules', response?.data?.length ? response.data : []);
            });
    }

    getDefaultdata(): Observable<any>{
        if(localStorage.getItem('dc_api_url')){
            const isDark = window.matchMedia('(prefers-color-scheme: dark)');
            if (isDark.matches) {
                this.faviconTag.href = 'assets/images/logo/zuper_logo_light.png';
            }
            else {
                this.faviconTag.href = 'https://s3.ap-south-1.amazonaws.com/images.zuper/icons/color_logomark.png';
            }
            return forkJoin([this._httpClient.get(`${this.baseUrl}/user/company`),
            this._httpClient.get(`${this.baseUrl}/user/preferences`),
            this._httpClient.get(`${this.baseUrl}/company/config`),
            this._httpClient.get(`${this.baseUrl}/company/policy`),
            this._httpClient.get(`${this.baseUrl}/company/modules`),
            this._httpClient.get(`${this.baseUrl}/user`)]).pipe(
                tap(([company,userPreference,companyConfig,companyPolicy,companyModule,user]: any)=>{
                    this._localStorageService.setLocalStorage('company',company.data);
                    this._localStorageService.setLocalStorage('user_preferences',userPreference.data);
                    this._localStorageService.setLocalStorage('company_configuration_data',companyConfig.data);
                    this._localStorageService.setLocalStorage('company_policy',companyPolicy.data);
                    this._localStorageService.setLocalStorage('user_modules',companyModule.data.user_modules || []);
                    this._localStorageService.setLocalStorage('company_modules',companyModule.data.company_modules);
                    this._localStorageService.setLocalStorage('user',user.data);
                    this._localStorageService.setLocalStorage('user_email',user.data?.email);
                    this._analyticsService.init();
                    this._checkIfCurrentUserZuperAgent(user?.data, companyPolicy?.data);
                    this._userService._user.next(user.data);
                    this.timezonePickerService.getAllTimeZones();
                    if(user?.data?.access_role){
                        this.getUserData(user.data);
                    }else{
                        this.getAndSetNavigation();
                    }
                    this.getSynchistoryModules();

                    if(window.sessionStorage.getItem('window_reloaded') === 'true'){
                        const module = this._localStorageService.getLocalStorage('current_module');
                        if(module){
                            this._localStorageService.constructMetaObject(module);
                        }
                    }else {
                        this._localStorageService.loadLocalStorageFromApi();
                    }
                    this._messagesService.getAll();
                    this._notificationsService.getAll();
                    this.utilityService.showProductPaymentAlert();
                    this.paymentService.getPaymentProviderData();
                    if(!this.popupShown && this.timezonePickerService.hasTimezoneDifference() && JSON.parse(this._localStorageService.getLocalStorage('show_timezone_popup'))) {
                      this.popupShown = true;
                      this.zuperDialogService.openCustom(TimezoneChangedPopupComponent).afterClosed().subscribe((result)=>{
                        if(result){
                          this.timezonePickerService.setShowTimezoneBadge(false);
                        }
                      });
                    }  
                })
              );
        }
    }

    private _checkIfCurrentUserZuperAgent(loggedInUser: any, companyPolicy: any) {
        // data?.user_uid
        const isVoiceEnabled = companyPolicy?.zuper_connect?.voice?.is_enabled;
        const isMessageEnabled = companyPolicy?.zuper_connect?.messaging?.is_enabled;
        const dcConnectURl = localStorage.getItem('dc_connect_url');
        let isCurrentUserZuperAgent = false;
        if ((isVoiceEnabled || isMessageEnabled) && dcConnectURl) {
            const url = `${dcConnectURl}/api/telephony/agent/${loggedInUser.user_uid}`;
            this._httpClient.get(url).pipe(
                finalize(() => {
                    isCurrentUserZuperAgent = isCurrentUserZuperAgent && loggedInUser?.role?.role_key === 'ADMIN'
                    if (isCurrentUserZuperAgent) {
                        this._localStorageService.setLocalStorage('is_zuper_agent', true);
                    } else {
                        this._localStorageService.removeLocalStorage('is_zuper_agent')
                    }
                    this._authService.isZuperAgentEnabled$.next(isCurrentUserZuperAgent);
                })
            ).
                subscribe({
                    next: (response: any) => {
                        isCurrentUserZuperAgent = response && response.type === 'success';
                    },
                    error: (_: any) => {
                        isCurrentUserZuperAgent = false;
                    },


                })
        } else {
            this._localStorageService.removeLocalStorage('is_zuper_agent');
            this._authService.isZuperAgentEnabled$.next(isCurrentUserZuperAgent);
        }
    }

}
